"use client"

import { useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Sprout, TrendingUp, BarChart3, Building2 } from "lucide-react"

const etapas = [
  {
    icon: Sprout,
    etapa: "Al abrir",
    problema:
      "Agendar tus primeros pacientes sin perder historiales ni citas en papel.",
  },
  {
    icon: TrendingUp,
    etapa: "Al crecer",
    problema:
      "Más pacientes significa más caos si agenda, cobros e inventario viven en lugares distintos.",
  },
  {
    icon: BarChart3,
    etapa: "Al escalar",
    problema:
      "Quieres saber si tu clínica es rentable, pero los números están repartidos en Excel.",
  },
  {
    icon: Building2,
    etapa: "Multi-sede",
    problema:
      "Cada sucursal opera distinto y pierdes control de lo que pasa en cada consultorio.",
  },
]

export function Journey() {
  const sectionRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const current = sectionRef.current

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting)
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px -10% 0px",
      }
    )

    if (current) {
      observer.observe(current)
    }

    return () => {
      if (current) observer.unobserve(current)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 bg-[#f0fafa]"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className={`max-w-3xl mx-auto text-center mb-10 sm:mb-14 transition-all duration-700 ease-out ${
            visible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <Badge
            className="mb-4 text-sm"
            style={{
              backgroundColor: "#e0f4f5",
              color: "#00676E",
              border: "none",
            }}
          >
            Tu clínica en cada etapa
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground mb-4">
            No solo es clínica. También es operación, cobros y control.
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Desde el primer paciente hasta varias sucursales: los mismos
            problemas operativos aparecen una y otra vez. Identifícalos antes
            de que te frenen.
          </p>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {etapas.map(({ icon: Icon, etapa, problema }, index) => (
            <div
              key={etapa}
              className={`group relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-6 transition-all duration-700 ease-out hover:-translate-y-3 hover:border-[#00676E]/30 hover:shadow-[0_20px_45px_rgba(0,103,110,0.15)]
              ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{
                transitionDelay: `${index * 180}ms`,
              }}
            >
              {/* Línea superior */}
              <div className="absolute left-0 top-0 h-1 w-0 bg-[#00676E] transition-all duration-500 group-hover:w-full" />

              {/* Glow */}
              <div
                className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: "#00676E22",
                }}
              />

              {/* Icono */}
              <div
                className="relative z-10 w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  backgroundColor: "#e0f4f5",
                  color: "#00676E",
                }}
              >
                <Icon className="h-6 w-6" />
              </div>

              <p className="relative z-10 text-xs font-semibold uppercase tracking-widest text-[#00676E] mb-3">
                {etapa}
              </p>

              <p className="relative z-10 text-sm leading-7 text-muted-foreground">
                {problema}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}